import {type Timestep} from '../../../util/plotHelpers/types';
import {type BrushEndMetadata} from '../../vis/highlight';
import {type DomainMaybe} from '../../vis/types';
import {type LinePlotSettings} from '../LinePlotSettingsContext';
import {type LinePlotZoomUsageEvent} from '../observability/zoomUsage';
import {type SharedPanelZoomState} from '../SharedPanelZoomContext';
import {type UserZoomIntentPayload} from '../types';
import {
  hasActiveZoom,
  planZoomEffects,
  selectZoomState,
  toChartXDomain,
  type ZoomSources,
} from './graphZoom';

/**
 * Zoom state and writers GraphContainer reads from the panel's zoom and time
 * contexts. The fidelity mode and x axis come from the settings. The setters
 * and handlers must be stable.
 */
export type GraphZoomInputs = Omit<ZoomSources, 'isFullFidelity'> & {
  setSharedZoom: (zoom: Partial<SharedPanelZoomState>) => void;
  setTimeFactor: (timeFactor: Timestep | null) => void;
  setZooming: (zooming: boolean) => void;
};

/** The settings the zoom slice reads. */
export type GraphZoomSettings = Pick<
  LinePlotSettings,
  'isFullFidelity' | 'xAxis' | 'xAxisIsDatetime'
>;

export type GraphZoom = {
  /** True when either axis is zoomed, before any gesture in progress. */
  activeZoom: boolean;
  /** Explicit query X boundaries used for issue edges and domain diagnostics. */
  isZoomedXMin: boolean;
  isZoomedXMax: boolean;
  xDomain: DomainMaybe | undefined;
  yDomain: DomainMaybe | undefined;
  onZoom: (
    zoom: UserZoomIntentPayload,
    brushEndMetadata?: BrushEndMetadata
  ) => void;
  zooming: boolean;
};

/**
 * Host preparation's zoom slice: the chart domains, the zooming flag, and the handler
 * for zoom gestures. It holds no React state, so it is a plain function.
 * Arguments are positional values, not a new object, so React Compiler can
 * cache the call on them before Graph renders.
 */
export function getGraphZoom(
  inputs: GraphZoomInputs,
  settings: GraphZoomSettings,
  timestep: Timestep | undefined,
  trackZoomUsage: (event: LinePlotZoomUsageEvent) => void
): GraphZoom {
  const {historySpaceXDomain, updateUserZoom, yDomain, zooming} =
    selectZoomState({
      ...inputs,
      isFullFidelity: settings.isFullFidelity,
    });
  const xDomain = toChartXDomain(historySpaceXDomain, {
    timestep,
    xAxisIsDatetime: settings.xAxisIsDatetime,
  });
  const activeZoom = hasActiveZoom(xDomain, yDomain);

  function onZoom(
    zoom: UserZoomIntentPayload,
    brushEndMetadata?: BrushEndMetadata
  ) {
    const effects = planZoomEffects(zoom, {
      activeZoom,
      brushEndMetadata,
      isFullFidelity: settings.isFullFidelity,
      timestep,
    });
    if (effects.shared != null) {
      inputs.setSharedZoom({xAxis: settings.xAxis, ...effects.shared});
    }
    if (effects.timeFactor !== 'keep') {
      inputs.setTimeFactor(effects.timeFactor);
    }
    if (effects.zooming !== 'keep') {
      inputs.setZooming(effects.zooming);
    }
    updateUserZoom(zoom);
    if (effects.usage != null) {
      trackZoomUsage(effects.usage);
    }
  }

  return {
    activeZoom,
    // Preserve query-bound semantics even when sampling supplies the display domain.
    isZoomedXMin: inputs.fullFidelity.queryZoom.xAxisMin != null,
    isZoomedXMax: inputs.fullFidelity.queryZoom.xAxisMax != null,
    xDomain,
    yDomain,
    onZoom,
    zooming,
  };
}
